import { test } from 'stencil-playwright';
import { expect } from '@playwright/test';
import {
  setUpLicenseApis,
  setUpSubscriptionApis,
  testCancelAtEndSubscriptionMessage,
  testCancelEndSubscription,
  testCancelLicense,
  testCancelNowSubscription,
  testCancelledLicenseMessage,
  testCancelledSubscriptionMessage,
  testErrorResponse,
} from '../../../../utilities/tests/salable-cancellation-button-tests';
import { mockSubscription } from '../../../../utilities/mock-data/subscription.mock';
import { mockLicense } from '../../../../utilities/mock-data/license.mock';
import { format } from 'date-fns';

test.describe('salable-cancellation-button Stencil E2E Tests', () => {
  const mockSessionToken = 'mock_session_token';
  const mockSubscriptionUuid = 'mock_subscription_uuid';
  const mockLicenseUuid = 'mock_license_uuid';

  test.describe('Success Cases', () => {
    test('Can cancel a license', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 200, data: mockLicense() },
          cancel: { status: 204 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockLicenseUuid}"
        ></salable-cancellation-button>
      `);

      await testCancelLicense(page);
    });

    test('Can cancel a subscription now', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 200, data: mockSubscription() },
          cancel: { status: 204 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);

      await testCancelNowSubscription(page);
    });

    test('Can cancel a subscription at period end', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 200, data: mockSubscription() },
          cancel: { status: 204 },
          reactivate: { status: 204 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);

      await testCancelEndSubscription(page, format(new Date('2030-02-01T14:48:00.000Z'), 'do MMM YYY'));
    });

    test('Correctly shows message for cancelled license', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 200, data: mockLicense({ status: 'CANCELED' }) },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockLicenseUuid}"
        ></salable-cancellation-button>
      `);

      await testCancelledLicenseMessage(page);
    });

    test('Correctly shows message for cancelled subscription', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 200, data: mockSubscription({ status: 'CANCELED' }) },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);

      await testCancelledSubscriptionMessage(page);
    });

    test('Correctly shows message for subscription due to be cancelled and can be reactivated', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 200, data: mockSubscription({ cancelAtPeriodEnd: true }) },
          reactivate: { status: 204 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);

      await testCancelAtEndSubscriptionMessage(page, format(new Date('2030-02-01T14:48:00.000Z'), 'do MMM YYY'));
    });
  });

  test.describe('Error Cases', () => {
    test('Displays an error for 401 response', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 401 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 401 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);
      await testErrorResponse({ page, errorMessage: 'Unauthorised' });
    });

    test('Displays an error for 403 response', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 403 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 403 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);
      await testErrorResponse({ page, errorMessage: 'Unauthorised' });
    });

    test('Displays an error for 404 response', async ({ page }) => {
      await setUpSubscriptionApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });
      await setUpLicenseApis({
        page,
        responses: {
          get: { status: 404 },
        },
      });

      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);
      await testErrorResponse({ page, errorMessage: 'License/subscription not found' });
    });

    test('Displays an error if the session-token prop is missing', async ({ page }) => {
      await page.setContent(`
        <salable-cancellation-button
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);
      await testErrorResponse({ page, errorMessage: 'Failed to load Cancellation Button' });
    });

    test('Displays an error if the uuid prop is missing', async ({ page }) => {
      await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
        ></salable-cancellation-button>
      `);
      await testErrorResponse({ page, errorMessage: 'Failed to load Cancellation Button' });
    });

    test.describe('cancel license request errors', () => {
      [
        { status: 401 as const, errorMessage: 'Unauthorised' },
        { status: 403 as const, errorMessage: 'Unauthorised' },
        { status: 404 as const, errorMessage: 'License not found' },
        { status: 500 as const, errorMessage: 'Failed to cancel license' },
      ].forEach(({ status, errorMessage }) => {
        test(status.toString(), async ({ page }) => {
          await setUpSubscriptionApis({
            page,
            responses: {
              get: { status: 404 },
            },
          });
          await setUpLicenseApis({
            page,
            responses: {
              get: { status: 200, data: mockLicense() },
              cancel: { status },
            },
          });

          await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockLicenseUuid}"
        ></salable-cancellation-button>
      `);

          await testErrorResponse({
            page,
            errorMessage,
            setupTests: async () => {
              const cancellationButton = page.locator('salable-cancellation-button');
              await expect(cancellationButton).toBeVisible();

              const cancelButton = page.getByText('Cancel license now');
              await expect(cancelButton).toBeVisible();

              await cancelButton.click();
            },
          });
        });
      });
    });

    test.describe('cancel subscription request errors', () => {
      [
        { status: 401 as const, errorMessage: 'Unauthorised' },
        { status: 403 as const, errorMessage: 'Unauthorised' },
        { status: 404 as const, errorMessage: 'Subscription not found' },
        { status: 500 as const, errorMessage: 'Failed to cancel subscription' },
      ].forEach(({ status, errorMessage }) => {
        test(status.toString(), async ({ page }) => {
          await setUpSubscriptionApis({
            page,
            responses: {
              get: { status: 200, data: mockSubscription() },
              cancel: { status },
            },
          });
          await setUpLicenseApis({
            page,
            responses: {
              get: { status: 404 },
            },
          });

          await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);

          await testErrorResponse({
            page,
            errorMessage,
            setupTests: async () => {
              const cancellationButton = page.locator('salable-cancellation-button');
              await expect(cancellationButton).toBeVisible();

              const cancelButton = page.getByText('Cancel subscription now');
              await expect(cancelButton).toBeVisible();

              await cancelButton.click();
            },
          });
        });
      });
    });

    test.describe('reactivate subscription request errors', () => {
      [
        { status: 401 as const, errorMessage: 'Unauthorised' },
        { status: 403 as const, errorMessage: 'Unauthorised' },
        { status: 404 as const, errorMessage: 'Subscription not found' },
        { status: 500 as const, errorMessage: 'Failed to reactivate subscription' },
      ].forEach(({ status, errorMessage }) => {
        test(status.toString(), async ({ page }) => {
          await setUpSubscriptionApis({
            page,
            responses: {
              get: { status: 200, data: mockSubscription({ cancelAtPeriodEnd: true }) },
              reactivate: { status },
            },
          });
          await setUpLicenseApis({
            page,
            responses: {
              get: { status: 404 },
            },
          });

          await page.setContent(`
        <salable-cancellation-button
          session-token="${mockSessionToken}"
          uuid="${mockSubscriptionUuid}"
        ></salable-cancellation-button>
      `);

          await testErrorResponse({
            page,
            errorMessage,
            setupTests: async () => {
              const cancellationButton = page.locator('salable-cancellation-button');
              await expect(cancellationButton).toBeVisible();

              const formattedDate = format(new Date('2030-02-01T14:48:00.000Z'), 'do MMM YYY');
              const reactivateButton = page.getByRole('button', {
                name: 'Reactivate Subscription',
              });

              await expect(page.getByText(`Your subscription will be cancelled on ${formattedDate}.`)).toBeVisible();
              await expect(reactivateButton).toBeVisible();

              await reactivateButton.click();
            },
          });
        });
      });
    });
  });
});
